import { GableType } from "../../models/GableType";
import { IInteriorProduct, IInteriorProductSubGroup } from "../../models/IChainInfo";
import { IItemInfo } from "../../models/IItemInfo";
import { intersect, IObj2d, subtract, topRight } from "../../models/IObj";
import { min } from "../../util/arraySlice";
import clamp from "../../util/clamp";
import { IInteriorItem, InteriorItem, InteriorProductType, IVec2d, Vec2d } from "../WebApiClient";
import { CabinetService } from "./CabinetService";
import { DragInfo, DragService, DragServiceProps } from "./DragService";
import { InteriorService } from "./InteriorService";

interface GableState {
    product: IInteriorProduct;
    gableSnapPositions: number[];

}
interface GableDragInfo extends DragInfo {
    valid: boolean;
}
interface FreeSpace {
    readonly min: number;
    readonly max: number;
}

export class DragServiceGable extends DragService<GableDragInfo> {

    private readonly gableState: GableState;
    private readonly isModule: boolean;
    private readonly nonDescendents: IInteriorItem[];
    private readonly minX: number;
    private readonly maxX: number;
    private readonly freeSpaces: FreeSpace[];
    private readonly productWidth: number;

    constructor(props: DragServiceProps) {
        super(props);
        const product = props.productSubGroup.products[0];
        const otherGableLikeStuff = this.baseState.otherGables.concat(this.baseState.otherModules);
        otherGableLikeStuff.sort((itemA, itemB) => itemA.item.position.x - itemB.item.position.x);
        this.productWidth = product.defaultSize.x;
        this.gableState = {
            product: product,
            gableSnapPositions: DragServiceGable.getGableSnapPositions(
                otherGableLikeStuff,
                props.cabinet.width,
                this.productWidth,
                props.cabinetService
            )
        }
        this.isModule = this.productSubGroup.productType === InteriorProductType.Module;
        const descendentIndexes = (props.item)
            ? InteriorService.getDescendentIndexes(props.item.index, props.cabinet.interiorItems)
            : new Set<number>();
        this.nonDescendents = props.cabinet.interiorItems.filter(ii =>
            ii.index !== props.item?.index
            && !descendentIndexes.has(ii.index));

        const gableInfo = props.cabinetService.getGableInfo();
        this.minX = gableInfo.left.type === GableType.Gable
            ? gableInfo.left.size.x + gableInfo.left.position.x
            : 0;
        const maxX = (gableInfo.right.type === GableType.Gable
            ? gableInfo.right.position.x
            : props.cabinet.width
        )
        this.maxX = maxX - this.productWidth;

        this.freeSpaces = getFreeSpaces(this.minX, maxX, this.nonDescendents)
            .map(fs => ({ min: fs.min, max: fs.max - this.productWidth }))
            .filter(fs => fs.min <= fs.max);
    }


    //overridden from GableService.
    //In addition to moving the gable itself, it should also move the items that are connected to it
    protected move(itemInfo: GableDragInfo): IInteriorItem[] {
        if (!this.item)
            return super.move(itemInfo);
        let itemsWithGableMoved = super.move(itemInfo);
        const gableTr = topRight(this.item);
        const gableMoveAmount = subtract(this.item.position, itemInfo.position);

        let result = itemsWithGableMoved.map(item => {
            if (item.index === this.item?.index)
                return item; //this is the gable itself, it's already moved

            const itemTr = topRight(item);
            if (item.position.x >= this.item!.position.x
                && item.position.y >= this.item!.position.y
                && itemTr.x <= gableTr.x
                && itemTr.y <= gableTr.y
            ) {
                //item is inside gable - move item by same amount as gable
                return {
                    ...item,
                    position: new Vec2d(subtract(item.position, gableMoveAmount)),
                };
            }
            //TODO move/resize items that are connected to gable/module on just one side

            return item;
        });
        return result.map(ii => new InteriorItem(ii));
    }

    static acceptable(pt: IInteriorProductSubGroup): boolean {
        return (
            pt.productType === InteriorProductType.Gable
            || pt.productType === InteriorProductType.Module
        );
    }

    getDragInfo(cursorModelPos: IVec2d, forceCopy: boolean): GableDragInfo {
        //TODO low priority: handle forceCopy
        const product = this.productSubGroup.products[0];
        const obj = this.getBestPos(cursorModelPos, product);



        const shadows = this.isModule
            ? []
            : this.gableState.gableSnapPositions.map(posX => ({
                position: { x: posX, y: this.floorY },
                size: product.defaultSize
            }));

        const productMaterial = product.materials.find(m => m.interiorMaterialId === this.cabinet.interiorMaterialId);
        const textureUrl = productMaterial?.drawingPath ?? product.textureUrl;
        return {
            ...obj,
            productId: product.id,
            textureUrl: textureUrl,
            shadows,
            valid: !!obj && !this.overlapsOthers(obj),
            parentIndexes: []
        };
    }

    place(dragInfo: GableDragInfo, forceCopy: boolean): IInteriorItem[] {
        if (!dragInfo.valid)
            return this.cabinet.interiorItems;
        return super.place(dragInfo, forceCopy);
    }

    private overlapsOthers(obj: IObj2d): boolean {
        return this.nonDescendents
            .some(otherItem => intersect(otherItem, obj));
    }

    private getBestPos(cursorModelPos: IVec2d, product: IInteriorProduct): IObj2d {
        const height = clamp(product.minSize.y, this.cabinet.height, product.maxSize.y);
        return {
            position: {
                x: this.getBestGableX(cursorModelPos),
                y: this.floorY
            },
            size: {
                x: product.defaultSize.x,
                y: height,
            }
        };

    }

    private getBestGableX(cursorModelPos: IVec2d): number {
        const snapDist = 50;
        const baseX = clamp(
            this.minX,
            Math.round(cursorModelPos.x - this.mouseOffset.x),
            this.maxX
        );


        if (this.gableState.gableSnapPositions.length > 0) {
            const orderedSnapPositions = [...this.gableState.gableSnapPositions]
                .sort((a, b) => Math.abs(a - baseX) - Math.abs(b - baseX))
                ;
            const best = orderedSnapPositions[0];
            if (Math.abs(best - baseX) < snapDist) {
                //current position is close to a snap position, return the snap position
                return best;
            }
        }
        //TODO handle no space for object
        const distances = this.freeSpaces.map(fs => ({
            fs,
            bestPosInSpace: clamp(fs.min, baseX, fs.max),
            dist: Math.abs(clamp(fs.min, baseX, fs.max) - baseX)
        }));
        const bestFreeSpace = min(distances, d => d.dist);
        const bestPos = bestFreeSpace?.bestPosInSpace;
        return bestPos ?? baseX;
    }

    private static getGableSnapPositions(otherGables: IItemInfo[], cabinetWidth: number, itemWidth: number, cabinetService: CabinetService): number[] {
        const positions: number[] = [];
        const gableInfo = cabinetService.getGableInfo();
        const leftOuterGableMaxX = gableInfo.left.type === GableType.Gable
            ? gableInfo.left.size.x + gableInfo.left.position.x
            : 0;
        const rightGableMinX = gableInfo.right.type === GableType.Gable
            ? gableInfo.right.position.x
            : cabinetWidth;

        let leftX = leftOuterGableMaxX;
        for (let gable of otherGables) {
            //get snap distances between previous gable and this gable
            const rightX = gable.item.position.x;
            positions.push(...this.getSnapPositions(leftX, rightX, itemWidth));
            leftX = gable.item.position.x + gable.item.size.x;
        }
        //get snap distance between last gable and outer gable
        positions.push(...this.getSnapPositions(leftX, rightGableMinX, itemWidth));
        return positions;
    }

    private static getSnapPositions(leftX: number, rightX: number, itemWidth: number): number[] {
        const snapDistances = [0, 367, 567, 767];
        const dist = rightX - leftX;
        const positions: number[] = [];
        for (let snapDist of snapDistances) {
            if (snapDist + itemWidth > dist)
                break;
            positions.push(leftX + snapDist);
            positions.push(rightX - snapDist - itemWidth);
        }
        return positions;
    }
}

function getFreeSpaces(minX: number, maxX: number, nonDescendents: IInteriorItem[]): FreeSpace[] {
    const result: FreeSpace[] = [];
    let freeStartX = minX;

    for (let item of nonDescendents) {
        if (freeStartX < item.position.x) {
            result.push({
                min: freeStartX,
                max: item.position.x
            });
            freeStartX = item.position.x + item.size.x;
        }
        else {
            freeStartX = Math.max(freeStartX, item.position.x + item.size.x);
        }
    }
    if (freeStartX < maxX) {
        result.push({
            min: freeStartX,
            max: maxX
        });
    }
    return result;
}
